import React from "react";

const Section = ({ title, children }) => (
  <section className="mt-10" data-testid={`privacy-section-${title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
    <h2 className="font-heading text-xl md:text-2xl font-light text-ink">{title}</h2>
    <div className="mt-3 space-y-3 text-[#5C6664] text-sm md:text-base leading-relaxed">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="space-y-2 pl-5 list-disc marker:text-iri-mid">
    {items.map((it, i) => (
      <li key={i}>{it}</li>
    ))}
  </ul>
);

export default function AppPrivacy() {
  return (
    <div className="min-h-screen bg-stone" data-testid="app-privacy-page">
      <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
        <p className="text-xs uppercase tracking-[0.2em] text-iri-mid">Spa Rewards App</p>
        <h1 className="font-heading text-4xl md:text-5xl font-light text-ink mt-3">Privacy Policy</h1>
        <p className="text-[#8A9492] text-sm mt-3" data-testid="privacy-updated">Last updated: August 2, 2026</p>

        <p className="mt-8 text-[#5C6664] text-sm md:text-base leading-relaxed">
          This Privacy Policy explains how Spa Rewards App, Noah Cannon, developer or affiliates
          (&ldquo;we&rdquo;, &ldquo;us&rdquo;) collects, uses and protects your information when you use the
          Spa Rewards mobile app (the &ldquo;App&rdquo;). Your App account is shared with our website, so
          information is kept in sync across both.
        </p>

        <Section title="Information we collect">
          <Bullets
            items={[
              <><strong className="text-ink font-medium">Account information:</strong> your name, email address, phone number (optional) and password (stored securely, never in plain text).</>,
              <><strong className="text-ink font-medium">Loyalty &amp; activity:</strong> reward points, membership tier, check-in history and redemptions.</>,
              <><strong className="text-ink font-medium">Purchases:</strong> orders you place in the App. Payments are processed by our payment provider, Stripe; we do not store your full card details.</>,
              <><strong className="text-ink font-medium">Camera:</strong> if you choose to scan a check-in QR code, the camera is used only on your device to read the code. We do not capture, store or transmit any photos or video.</>,
              <><strong className="text-ink font-medium">AI advisor messages:</strong> questions you send to the in-app skincare advisor are processed to generate a response.</>,
            ]}
          />
        </Section>

        <Section title="How we use your information">
          <Bullets
            items={[
              "To create and manage your account and membership.",
              "To award, track and let you redeem reward points.",
              "To process purchases and provide order history.",
              "To provide skincare guidance through the AI advisor.",
              "To operate, secure and improve the App.",
            ]}
          />
        </Section>

        <Section title="How we share information">
          <Bullets
            items={[
              "Service providers who help us run the App, including our payment processor (Stripe) and our hosting/AI infrastructure providers, only as needed to provide the service.",
              "We do not sell your personal information.",
              "We may disclose information if required by law.",
            ]}
          />
        </Section>

        <Section title="Permissions">
          <p>
            <strong className="text-ink font-medium">Camera:</strong> requested only when you tap to scan a
            check-in QR code. You can decline and enter the code manually, or change this anytime in your
            device Settings.
          </p>
        </Section>

        <Section title="Data retention & deletion">
          <p>
            We keep your information for as long as your account is active or as needed to provide the service
            and meet legal obligations. You can permanently delete your account and associated data at any time
            from within the App: <strong className="text-ink font-medium">Profile &rarr; Delete account</strong>.
            You may also request deletion by contacting us below.
          </p>
        </Section>

        <Section title="Your choices">
          <Bullets
            items={[
              "Access or update your profile in the App.",
              "Manage camera permission in your device Settings.",
              "Delete your account and data from Profile → Delete account, or by contacting us.",
            ]}
          />
        </Section>

        <Section title="Children's privacy">
          <p>The App is intended for users 18 and older and is not directed to children.</p>
        </Section>

        <Section title="Security">
          <p>
            We use industry-standard measures, including encrypted passwords and secure token storage, to
            protect your information. No method of transmission or storage is 100% secure.
          </p>
        </Section>

        <Section title="Changes to this policy">
          <p>
            We may update this policy from time to time. Material changes will be reflected by the
            &ldquo;Last updated&rdquo; date above.
          </p>
        </Section>

        <Section title="Contact us">
          <p>
            Spa Rewards, Noah Cannon, Developer<br />
            42855 Garfield Rd. 105<br />
            Clinton Twp, MI 48038 USA
          </p>
          <p>
            Email:{" "}
            <a href="mailto:privacy@spasolutions.com" className="text-ink underline" data-testid="privacy-email">
              privacy@spasolutions.com
            </a>
            <br />
            Phone:{" "}
            <a href="tel:+15866908693" className="text-ink underline" data-testid="privacy-phone">
              586-690-8693
            </a>
          </p>
        </Section>
      </div>
    </div>
  );
}
